<script>
/**
 * CanalComposer — composer para publicar em Canais do WhatsApp (newsletter).
 * Espelho de GrupoComposer mas chama endpoints /newsletter/* e exige is_admin.
 *
 * Endpoints:
 *   - texto:  POST /newsletter/publicar/:id   { texto }
 *   - mídia:  POST /newsletter/midia/:id      multipart { file, tipo, caption }
 *   - áudio:  POST /newsletter/audio/:id      multipart { file }
 *
 * Props:
 *   - newsletterId
 *   - isAdmin (default false) — se false, esconde tudo e mostra placeholder
 *   - disabled
 */
import axios from 'axios';

const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😅', '😊', '😇', '🙂', '😉', '😍', '😘', '🥰', '😎', '🤩', '🥳',
  '🤔', '🤨', '😐', '😶', '🙄', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😢', '😭', '😤', '😠', '😡',
  '👍', '👎', '👏', '🙌', '🤝', '👋', '🙏', '💪', '👌', '✌️', '🤞', '✊',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '💕', '✨', '🔥', '⭐', '🎉', '🎊', '🎁',
  '✅', '❌', '⚠️', '❗', '❓', '💯', '🆗', '🆘',
];

export default {
  name: 'CanalComposer',
  props: {
    newsletterId: { type: [Number, String], default: null },
    isAdmin: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ['enviado', 'erro', 'abrir-enquete', 'abrir-teste'],
  data() {
    return {
      texto: '',
      enviando: false,
      mostrarMenuMais: false,
      arquivoSelecionado: null,
      previewArquivo: null,
      tipoSelecionado: null,
      caption: '',
      gravando: false,
      pausado: false,
      audioBlob: null,
      audioUrl: null,
      mediaRecorder: null,
      tempoGravacao: 0,
      timerGravacao: null,
      enviarAposStop: false,
      mostrarEmoji: false,
      emojis: EMOJIS,
    };
  },
  computed: {
    mostrarBotaoMicrofone() {
      return !this.texto.trim() && !this.gravando && !this.audioBlob;
    },
    emModoGravacao() {
      return this.gravando || this.pausado || Boolean(this.audioBlob);
    },
    duracaoFormatada() {
      const t = this.tempoGravacao;
      const mm = String(Math.floor(t / 60)).padStart(2, '0');
      const ss = String(t % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    },
  },
  mounted() {
    document.addEventListener('click', this._onClickFora);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._onClickFora);
    if (this.timerGravacao) clearInterval(this.timerGravacao);
    if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
    if (this.previewArquivo) URL.revokeObjectURL(this.previewArquivo);
  },
  methods: {
    _focar() { if (this.$refs.textarea) this.$refs.textarea.focus(); },
    _onClickFora(e) {
      if (this.mostrarMenuMais && this.$refs.menuMaisBox && !this.$refs.menuMaisBox.contains(e.target)) {
        this.mostrarMenuMais = false;
      }
      if (this.mostrarEmoji && this.$refs.emojiBox && !this.$refs.emojiBox.contains(e.target)) {
        this.mostrarEmoji = false;
      }
    },

    async enviarTexto() {
      if (!this.texto.trim() || !this.newsletterId || this.enviando) return;
      const texto = this.texto;
      this.enviando = true;
      try {
        await axios.post(`/newsletter/publicar/${this.newsletterId}`, { texto });
        this.texto = '';
        this.$emit('enviado', { tipo: 'texto', conteudo: texto });
      } catch (error) {
        this.$emit('erro', this._erroMsg(error));
      } finally {
        this.enviando = false;
        this.$nextTick(() => this._focar());
      }
    },
    onKeyDown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.enviarTexto();
      }
    },

    formatar(prefixo, sufixo) {
      const el = this.$refs.textarea;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const sel = this.texto.substring(start, end);
      const novo = `${this.texto.substring(0, start)}${prefixo}${sel || 'texto'}${sufixo}${this.texto.substring(end)}`;
      this.texto = novo;
      this.$nextTick(() => {
        el.focus();
        el.setSelectionRange(start + prefixo.length, end + prefixo.length);
      });
    },
    aplicarFormatacao(prefixo, sufixo) {
      this.mostrarMenuMais = false;
      this.formatar(prefixo, sufixo);
    },

    inserirEmoji(emoji) {
      const el = this.$refs.textarea;
      const pos = el ? el.selectionStart : this.texto.length;
      this.texto = `${this.texto.substring(0, pos)}${emoji}${this.texto.substring(pos)}`;
      this.mostrarEmoji = false;
      this.$nextTick(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(pos + emoji.length, pos + emoji.length);
        }
      });
    },

    abrirAnexoArquivo() { this.mostrarMenuMais = false; this.$refs.inputArquivo.click(); },
    abrirAnexoImagem() { this.mostrarMenuMais = false; this.$refs.inputImagem.click(); },
    chamarEnquete() { this.mostrarMenuMais = false; this.$emit('abrir-enquete'); },
    chamarTeste() { this.mostrarMenuMais = false; this.$emit('abrir-teste'); },

    onArquivoEscolhido(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.arquivoSelecionado = file;
      if (file.type.startsWith('image/')) this.tipoSelecionado = 'imagem';
      else if (file.type.startsWith('video/')) this.tipoSelecionado = 'video';
      else if (file.type.startsWith('audio/')) this.tipoSelecionado = 'audio';
      else this.tipoSelecionado = 'documento';
      if (this.tipoSelecionado === 'imagem' || this.tipoSelecionado === 'video') {
        this.previewArquivo = URL.createObjectURL(file);
      } else {
        this.previewArquivo = null;
      }
      this.caption = '';
      event.target.value = '';
    },
    cancelarArquivo() {
      if (this.previewArquivo) URL.revokeObjectURL(this.previewArquivo);
      this.arquivoSelecionado = null;
      this.previewArquivo = null;
      this.tipoSelecionado = null;
      this.caption = '';
    },
    async enviarArquivo() {
      if (!this.arquivoSelecionado || !this.newsletterId) return;
      this.enviando = true;
      try {
        const form = new FormData();
        form.append('file', this.arquivoSelecionado);
        form.append('tipo', this.tipoSelecionado);
        if (this.caption) form.append('caption', this.caption);
        await axios.post(`/newsletter/midia/${this.newsletterId}`, form);
        this.cancelarArquivo();
        this.$emit('enviado', { tipo: this.tipoSelecionado });
      } catch (error) {
        this.$emit('erro', this._erroMsg(error));
      } finally {
        this.enviando = false;
      }
    },

    async iniciarGravacao() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const candidatos = [
          'audio/ogg;codecs=opus', 'audio/ogg',
          'audio/mp4;codecs=mp4a.40.2', 'audio/mp4',
          'audio/mpeg',
        ];
        let mimeType = '';
        for (const m of candidatos) {
          if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) {
            if (!mimeType) mimeType = m;
          }
        }
        const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        const chunks = [];
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        mr.onstop = () => {
          const tipoFinal = mr.mimeType || mimeType || 'audio/ogg';
          this.audioBlob = new Blob(chunks, { type: tipoFinal });
          this.audioUrl = URL.createObjectURL(this.audioBlob);
          stream.getTracks().forEach((t) => t.stop());
          if (this.enviarAposStop) {
            this.enviarAposStop = false;
            this.enviarAudio();
          }
        };
        mr.start();
        this.mediaRecorder = mr;
        this.gravando = true;
        this.tempoGravacao = 0;
        this.timerGravacao = setInterval(() => { this.tempoGravacao += 1; }, 1000);
      } catch (error) {
        this.$emit('erro', `Não foi possível acessar o microfone: ${error.message}`);
      }
    },
    pararGravacao() {
      if (this.mediaRecorder && this.gravando) this.mediaRecorder.stop();
      this.gravando = false;
      this.pausado = false;
      clearInterval(this.timerGravacao);
    },
    togglePausar() {
      if (!this.mediaRecorder || !this.gravando) return;
      if (this.pausado) {
        try { this.mediaRecorder.resume(); } catch (_e) { /* noop */ }
        this.pausado = false;
        this.timerGravacao = setInterval(() => { this.tempoGravacao += 1; }, 1000);
      } else {
        try { this.mediaRecorder.pause(); } catch (_e) { /* noop */ }
        this.pausado = true;
        clearInterval(this.timerGravacao);
      }
    },
    descartarGravacao() {
      const mr = this.mediaRecorder;
      if (mr && (this.gravando || this.pausado)) {
        this.enviarAposStop = false;
        mr.onstop = () => {
          try { mr.stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        };
        try { mr.stop(); } catch (_e) { /* noop */ }
      }
      this._limparGravacao();
    },
    finalizarEEnviarAudio() {
      if (this.gravando || this.pausado) {
        this.enviarAposStop = true;
        this.pararGravacao();
      } else if (this.audioBlob) {
        this.enviarAudio();
      }
    },
    _limparGravacao() {
      this.gravando = false;
      this.pausado = false;
      this.enviarAposStop = false;
      clearInterval(this.timerGravacao);
      if (this.audioUrl) URL.revokeObjectURL(this.audioUrl);
      this.audioBlob = null;
      this.audioUrl = null;
      this.tempoGravacao = 0;
      if (this.mediaRecorder) {
        try { this.mediaRecorder.stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        this.mediaRecorder = null;
      }
    },
    async enviarAudio() {
      if (!this.audioBlob || !this.newsletterId) return;
      this.enviando = true;
      try {
        const tipoBlob = (this.audioBlob.type || 'audio/ogg').split(';')[0];
        const ext = tipoBlob.includes('ogg') ? 'ogg'
          : tipoBlob.includes('mp4') ? 'm4a'
            : tipoBlob.includes('mpeg') ? 'mp3'
              : tipoBlob.includes('webm') ? 'webm'
                : 'ogg';
        const filename = `audio-${Date.now()}.${ext}`;
        const form = new FormData();
        form.append('file', this.audioBlob, filename);
        await axios.post(`/newsletter/audio/${this.newsletterId}`, form);
        this._limparGravacao();
        this.$emit('enviado', { tipo: 'audio' });
      } catch (error) {
        this.$emit('erro', this._erroMsg(error));
      } finally {
        this.enviando = false;
      }
    },

    _erroMsg(error) {
      const data = error.response && error.response.data;
      return (data && (data.details || data.error)) || error.message;
    },
  },
};
</script>

<template>
  <!-- Só admins publicam: para seguidores, o composer não renderiza (sem barra inferior). -->
  <div v-if="isAdmin" class="composer-wrapper border-top">
      <!-- Preview de arquivo selecionado -->
      <div v-if="arquivoSelecionado" class="preview-anexo p-2 border-bottom">
        <div class="d-flex align-items-start gap-2">
          <img v-if="tipoSelecionado === 'imagem'" :src="previewArquivo" class="preview-img" alt="preview" />
          <video v-else-if="tipoSelecionado === 'video'" :src="previewArquivo" class="preview-img" controls></video>
          <div v-else class="preview-doc">
            <i class="ri-file-line" style="font-size: 2rem;"></i>
            <div class="small text-truncate">{{ arquivoSelecionado.name }}</div>
          </div>
          <div class="flex-grow-1">
            <input
              v-model="caption"
              type="text"
              class="form-control form-control-sm"
              placeholder="Legenda (opcional)"
              @keydown.enter="enviarArquivo"
            />
            <div class="d-flex gap-1 mt-1">
              <button type="button" class="btn btn-success btn-sm" :disabled="enviando" @click="enviarArquivo">
                <i class="ri-send-plane-2-line me-1"></i>Publicar
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelarArquivo">Cancelar</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Emoji picker -->
      <div v-if="mostrarEmoji" ref="emojiBox" class="emoji-picker">
        <button v-for="(e, i) in emojis" :key="i" type="button" class="emoji-btn" @click="inserirEmoji(e)">{{ e }}</button>
      </div>

      <!-- Barra principal -->
      <div v-if="!emModoGravacao" class="composer composer-wa">
        <div class="composer-action-wrapper">
          <button type="button" class="composer-action-btn" :disabled="disabled" title="Anexar / formatar" @click.stop="mostrarMenuMais = !mostrarMenuMais">
            <i :class="mostrarMenuMais ? 'ri-close-line' : 'ri-add-line'"></i>
          </button>
          <div v-if="mostrarMenuMais" ref="menuMaisBox" class="composer-popup composer-popup--menu" @click.stop>
            <button type="button" class="popup-item" @click="abrirAnexoArquivo">
              <i class="ri-attachment-2 popup-item-icon"></i><span>Documento</span>
            </button>
            <button type="button" class="popup-item" @click="abrirAnexoImagem">
              <i class="ri-image-line popup-item-icon"></i><span>Foto / Vídeo</span>
            </button>
            <button type="button" class="popup-item" @click="chamarEnquete">
              <i class="ri-bar-chart-horizontal-line popup-item-icon"></i><span>Enquete</span>
            </button>
            <button type="button" class="popup-item" @click="chamarTeste">
              <i class="ri-trophy-line popup-item-icon"></i><span>Teste</span>
            </button>
            <div class="popup-divisor"></div>
            <div class="popup-formatos">
              <button type="button" class="popup-formato" title="Negrito (*texto*)" @click="aplicarFormatacao('*', '*')"><i class="ri-bold"></i></button>
              <button type="button" class="popup-formato" title="Itálico (_texto_)" @click="aplicarFormatacao('_', '_')"><i class="ri-italic"></i></button>
              <button type="button" class="popup-formato" title="Tachado (~texto~)" @click="aplicarFormatacao('~', '~')"><i class="ri-strikethrough"></i></button>
              <button type="button" class="popup-formato" title="Monoespaçado" @click="aplicarFormatacao('```', '```')"><i class="ri-code-line"></i></button>
            </div>
          </div>
        </div>

        <button type="button" class="composer-action-btn" :disabled="disabled" title="Emoji" @click.stop="mostrarEmoji = !mostrarEmoji">
          <i class="ri-emotion-happy-line"></i>
        </button>

        <input ref="inputArquivo" type="file" class="d-none" @change="onArquivoEscolhido" />
        <input ref="inputImagem" type="file" accept="image/*,video/*" capture="environment" class="d-none" @change="onArquivoEscolhido" />

        <textarea
          ref="textarea"
          v-model="texto"
          class="composer-input"
          rows="1"
          placeholder="Escreva uma publicação..."
          :disabled="disabled || enviando"
          @keydown="onKeyDown"
        ></textarea>

        <button v-if="mostrarBotaoMicrofone" type="button" class="btn composer-send-btn composer-mic-btn" :disabled="disabled || enviando" title="Gravar áudio" @click="iniciarGravacao">
          <i class="ri-mic-line"></i>
        </button>
        <button v-else type="button" class="btn composer-send-btn" :disabled="enviando || !texto.trim()" title="Publicar" @click="enviarTexto">
          <i v-if="enviando" class="ri-loader-4-line spin"></i>
          <i v-else class="ri-send-plane-2-line"></i>
        </button>
      </div>

      <!-- Barra de gravação -->
      <div v-else class="recording-bar">
        <button type="button" class="recording-btn recording-btn--ghost" title="Descartar" :disabled="enviando" @click="descartarGravacao">
          <i class="ri-delete-bin-line"></i>
        </button>
        <div class="recording-meta">
          <span class="rec-dot" :class="{ 'rec-dot--pausado': pausado || !gravando }"></span>
          <span class="recording-time">{{ duracaoFormatada }}</span>
        </div>
        <div class="recording-waveform" :class="{ 'recording-waveform--ativo': gravando && !pausado }" aria-hidden="true">
          <span v-for="n in 36" :key="n" :style="{ animationDelay: `${(n % 6) * 0.1}s` }"></span>
        </div>
        <button v-if="gravando" type="button" class="recording-btn recording-btn--ghost" :title="pausado ? 'Retomar' : 'Pausar'" @click="togglePausar">
          <i :class="pausado ? 'ri-play-fill' : 'ri-pause-fill'"></i>
        </button>
        <button type="button" class="recording-btn recording-btn--send" :disabled="enviando" title="Publicar áudio" @click="finalizarEEnviarAudio">
          <i v-if="enviando" class="ri-loader-4-line spin"></i>
          <i v-else class="ri-send-plane-2-line"></i>
        </button>
      </div>
  </div>
</template>

<style scoped>
.composer-wrapper { background: var(--vz-body-bg); border-color: var(--vz-border-color); position: relative; padding-bottom: env(safe-area-inset-bottom, 0px); }
.composer { background: var(--vz-body-bg); }

.composer-wa { display: flex; align-items: flex-end; gap: 0.35rem; padding: 0.5rem 0.75rem; background: var(--vz-body-bg); }
.composer-action-wrapper { position: relative; display: inline-flex; }
.composer-action-btn {
  width: 40px; height: 40px;
  border: 0; background: transparent;
  color: var(--vz-secondary-color);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.35rem; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s, color 0.15s; padding: 0;
}
.composer-action-btn:hover:not(:disabled) { background: var(--vz-tertiary-bg); color: var(--vz-primary); }
.composer-action-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.composer-input {
  flex: 1; min-width: 0; resize: none; max-height: 120px;
  border: 1px solid var(--vz-border-color); border-radius: 22px;
  padding: 0.55rem 1rem; font-size: 0.9rem; line-height: 1.4;
  background: #fff; color: var(--vz-body-color);
  outline: none; transition: border-color 0.15s, box-shadow 0.15s;
}
.composer-input:focus { border-color: #c9ced4; box-shadow: none; }
.composer-input:disabled { background: var(--vz-tertiary-bg); color: var(--vz-secondary-color); cursor: not-allowed; }

.composer-popup {
  position: absolute; bottom: calc(100% + 6px); left: 0;
  background: var(--vz-body-bg); border: 1px solid var(--vz-border-color); border-radius: 0.6rem;
  padding: 0.35rem; box-shadow: 0 6px 24px rgba(0,0,0,0.18); z-index: 6; min-width: 200px;
}
.popup-item {
  display: flex; align-items: center; gap: 0.6rem; width: 100%;
  border: 0; background: transparent; color: var(--vz-body-color);
  padding: 0.5rem 0.7rem; border-radius: 0.4rem; cursor: pointer;
  font-size: 0.875rem; text-align: left;
}
.popup-item:hover:not(:disabled) { background: var(--vz-tertiary-bg); }
.popup-item-icon { font-size: 1.1rem; color: var(--vz-primary); width: 1.25rem; text-align: center; }
.popup-divisor { height: 1px; background: var(--vz-border-color); margin: 0.3rem 0.2rem; }
.popup-formatos { display: flex; gap: 2px; padding: 0.2rem 0.3rem; }
.popup-formato {
  flex: 1; border: 0; background: transparent;
  color: var(--vz-secondary-color); font-size: 1rem;
  padding: 0.45rem 0; border-radius: 0.35rem; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.popup-formato:hover { background: var(--vz-tertiary-bg); color: var(--vz-primary); }

.composer-send-btn {
  background: var(--vz-primary); border-color: var(--vz-primary); color: #fff;
  height: 40px; width: 40px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0; font-size: 1.05rem; line-height: 1;
}
.composer-send-btn:hover:not(:disabled) { background: var(--brand-primary-hover, #7a0e14); color: #fff; }
.composer-send-btn:disabled { opacity: 0.5; }

.preview-anexo { background: var(--vz-tertiary-bg); }
.preview-img { width: 80px; height: 80px; object-fit: cover; border-radius: 0.375rem; flex-shrink: 0; }
.preview-doc {
  width: 80px; height: 80px; background: var(--vz-body-bg); border: 1px solid var(--vz-border-color); border-radius: 0.375rem;
  display: flex; flex-direction: column; align-items: center; justify-content: center; flex-shrink: 0; padding: 0.25rem;
}
.preview-doc .small { width: 100%; text-align: center; font-size: 0.65rem; }

.recording-bar { display: flex; align-items: center; gap: 0.6rem; padding: 0.5rem 0.75rem; background: var(--vz-body-bg); min-height: 56px; }
.recording-btn { width: 40px; height: 40px; border-radius: 50%; border: 0; display: inline-flex; align-items: center; justify-content: center; font-size: 1.25rem; cursor: pointer; flex-shrink: 0; }
.recording-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.recording-btn--ghost { background: transparent; color: var(--vz-secondary-color); }
.recording-btn--ghost:hover:not(:disabled) { background: var(--vz-tertiary-bg); color: var(--vz-body-color); }
.recording-btn--send { background: var(--vz-primary); color: #fff; }
.recording-btn--send:hover:not(:disabled) { filter: brightness(0.92); }

.recording-meta { display: inline-flex; align-items: center; gap: 0.4rem; flex-shrink: 0; min-width: 64px; }
.recording-time { font-size: 0.875rem; color: var(--vz-body-color); font-variant-numeric: tabular-nums; font-weight: 500; }
.rec-dot { width: 10px; height: 10px; background: #dc3545; border-radius: 50%; animation: rec-pulse 1.2s ease-in-out infinite; flex-shrink: 0; }
.rec-dot--pausado { animation: none; opacity: 0.5; }
@keyframes rec-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.45; transform: scale(0.85); } }

.recording-waveform { display: flex; align-items: center; flex-grow: 1; height: 28px; gap: 3px; overflow: hidden; min-width: 0; }
.recording-waveform span { display: block; width: 2px; height: 4px; background: var(--vz-secondary-color); border-radius: 2px; flex-shrink: 0; opacity: 0.55; }
.recording-waveform--ativo span { background: var(--vz-primary); opacity: 1; animation: rec-wave 1.1s ease-in-out infinite; }
@keyframes rec-wave {
  0%, 100% { height: 4px; }
  20%      { height: 14px; }
  40%      { height: 22px; }
  60%      { height: 10px; }
  80%      { height: 18px; }
}

.emoji-picker {
  position: absolute; bottom: calc(100% + 6px); left: 0.5rem;
  background: var(--vz-body-bg); border: 1px solid var(--vz-border-color);
  border-radius: 0.5rem; padding: 0.5rem;
  display: grid; grid-template-columns: repeat(8, 1fr); gap: 0.15rem;
  max-width: 280px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 5;
}
.emoji-btn { background: transparent; border: 0; font-size: 1.25rem; cursor: pointer; padding: 0.2rem; border-radius: 0.25rem; transition: background 0.1s; }
.emoji-btn:hover { background: var(--vz-tertiary-bg); }

.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

[data-bs-theme="dark"] .composer-wrapper,
[data-bs-theme="dark"] .composer,
[data-bs-theme="dark"] .composer-wa { background: #14181d; }
[data-bs-theme="dark"] .composer-input { background: #2a3038; border-color: #3a4049; color: #e6e8eb; }
[data-bs-theme="dark"] .composer-input:focus { border-color: #4a525c; }
[data-bs-theme="dark"] .composer-input:disabled { background: #181c22; color: #7a8089; }
[data-bs-theme="dark"] .composer-action-btn { color: #b8bdc4; }
[data-bs-theme="dark"] .composer-action-btn:hover:not(:disabled) { background: #1f252c; color: #ff9aa5; }
[data-bs-theme="dark"] .composer-popup { background: #1c2128; border-color: #2a3038; box-shadow: 0 6px 24px rgba(0,0,0,0.5); }
[data-bs-theme="dark"] .popup-item { color: #e6e8eb; }
[data-bs-theme="dark"] .popup-item:hover:not(:disabled) { background: #262c34; }
[data-bs-theme="dark"] .popup-divisor { background: #2a3038; }
[data-bs-theme="dark"] .popup-formato { color: #b8bdc4; }
[data-bs-theme="dark"] .popup-formato:hover { background: #262c34; color: #ff9aa5; }
[data-bs-theme="dark"] .emoji-picker { background: #1c2128; border-color: #2a3038; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
[data-bs-theme="dark"] .emoji-btn:hover { background: #262c34; }
[data-bs-theme="dark"] .preview-anexo { background: #1c2128; }
[data-bs-theme="dark"] .recording-bar { background: #14181d; }
</style>
